/**
 * Documents Page Component
 *
 * Document management interface for uploading and organizing files.
 * Integrates DocumentUploader, DocumentList, and VersionHistory components.
 * Handles document selection, download, delete, and upload version actions.
 * Includes processing workflow for SORA evidence extraction.
 *
 * Requirements: 2.1, 2.5, 3.2, 3.5, 3.6
 */

import React, { useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowLeftRegular,
  Warning24Regular,
  Dismiss24Regular,
  ArrowDownload20Regular,
  Delete20Regular,
  ArrowUpload20Regular,
  Eye20Regular,
  DocumentMultiple20Regular,
} from '@fluentui/react-icons';
import { useDocuments } from '../../contexts/DocumentContext';
import { useExtraction } from '../../contexts/ExtractionContext';
import { DocumentUploader } from '../../components/DocumentUploader';
import { DocumentList } from '../../components/DocumentList';
import { VersionHistory } from '../../components/VersionHistory';
// import { ProcessingButton } from '../../components/ProcessingButton';
import { ProcessingProgress } from '../../components/ProcessingProgress';
import { isDemoMode, config } from '../../config';
import { createApiClient, isApiSuccess, getErrorMessage } from '../../services/apiClient';
import { getMockDataService } from '../../services/mockDataService';
import type {
  DocumentMetadata,
  UploadedDocument,
  DocumentVersion,
  ProcessingStatus,
  ExtractionResults,
} from '../../types';
import type { ProcessedDocument } from '../../types/documents';
import styles from './DocumentsPage.module.css';

// ============================================================================
// Types
// ============================================================================

export interface DocumentsPageProps {}

interface DeleteConfirmationState {
  isOpen: boolean;
  documentId: string | null;
  documentName: string;
}

interface UploadVersionState {
  isOpen: boolean;
  documentId: string | null;
  documentName: string;
}

interface ProcessingState {
  isProcessing: boolean;
  showProgress: boolean;
  status: ProcessingStatus;
  progress: number;
  currentStep: string;
  error: string | null;
  extractionId: string | null;
}

const INITIAL_PROCESSING_STATE: ProcessingState = {
  isProcessing: false,
  showProgress: false,
  status: 'pending',
  progress: 0,
  currentStep: '',
  error: null,
  extractionId: null,
};

// ============================================================================
// Component
// ============================================================================

/**
 * Documents Page - document management interface
 *
 * Features:
 * - Document upload with drag-and-drop (Req 2.2, 2.3)
 * - Document list with name, type, upload date, version (Req 2.1)
 * - Version history viewer when document selected (Req 2.7)
 * - Document actions: download, upload new version, delete (Req 2.5)
 * - Document details view when :id param is present
 * - Processing workflow for SORA evidence extraction (Req 3.2, 3.5, 3.6)
 */
export const DocumentsPage: React.FC<DocumentsPageProps> = () => {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();

  // Auth0 hook for API authentication
  const { getAccessTokenSilently } = useAuth0();

  // Document context
  const {
    documents,
    isLoading,
    error,
    uploadDocument,
    deleteDocument,
    uploadNewVersion,
    downloadDocument,
    refreshDocuments,
    getDocumentById,
    clearError,
  } = useDocuments();

  // Extraction context for storing results
  const { setCurrentExtraction } = useExtraction();

  // Local state
  const [selectedDocument, setSelectedDocument] = useState<ProcessedDocument | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState<DeleteConfirmationState>({
    isOpen: false,
    documentId: null,
    documentName: '',
  });
  const [uploadVersionState, setUploadVersionState] = useState<UploadVersionState>({
    isOpen: false,
    documentId: null,
    documentName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Selection state for document comparison
  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(new Set());

  // Processing state (Req 3.2, 3.5)
  const [processingState, setProcessingState] = useState<ProcessingState>(INITIAL_PROCESSING_STATE);

  // Ref for version upload input
  const versionUploadRef = useRef<HTMLInputElement>(null);

  // Memoized services for processing
  const apiClient = useMemo(() => {
    if (isDemoMode()) {
      return null;
    }
    return createApiClient(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  const mockService = useMemo(() => {
    if (isDemoMode()) {
      return getMockDataService();
    }
    return null;
  }, []);

  // ============================================================================
  // Handlers
  // ============================================================================

  /**
   * Handle document selection - show version history panel
   */
  const handleDocumentSelect = useCallback((document: ProcessedDocument) => {
    setSelectedDocument(document);
    setActionError(null);
  }, []);

  /**
   * Close version history panel
   */
  const handleCloseVersionHistory = useCallback(() => {
    setSelectedDocument(null);
  }, []);

  /**
   * Handle document download
   * Requirement 12.3: Document download operations are simulated in demo mode
   */
  const handleDownload = useCallback(async (documentId: string) => {
    const doc = getDocumentById(documentId);
    if (!doc) {
      setActionError('Unable to download document. Document not found.');
      return;
    }

    try {
      // Use the context's downloadDocument which handles demo mode simulation
      const blob = await downloadDocument(documentId);
      if (blob) {
        // Create a download link for the blob
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.processedFileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else {
        setActionError('Unable to download document. Please try again.');
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Unable to download document. Please try again.'
      );
    }
  }, [getDocumentById, downloadDocument]);

  /**
   * Handle version download
   * Requirement 12.3: Document download operations are simulated in demo mode
   */
  const handleVersionDownload = useCallback(async (version: DocumentVersion) => {
    if (!selectedDocument) {
      return;
    }

    try {
      // Use the context's downloadDocument which handles demo mode simulation
      const blob = await downloadDocument(selectedDocument.processId);
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${selectedDocument.processedFileName || 'document'}_v${version.version}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Unable to download version. Please try again.'
      );
    }
  }, [selectedDocument, downloadDocument]);

  /**
   * Open delete confirmation dialog
   */
  const handleDeleteClick = useCallback((documentId: string) => {
    const doc = getDocumentById(documentId);
    setDeleteConfirmation({
      isOpen: true,
      documentId,
      documentName: doc?.processedFileName || 'this document',
    });
  }, [getDocumentById]);

  /**
   * Confirm and execute document deletion
   */
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirmation.documentId) return;

    setIsDeleting(true);
    setActionError(null);

    try {
      await deleteDocument(deleteConfirmation.documentId);

      // If the deleted document was selected, clear selection
      if (selectedDocument?.processId === deleteConfirmation.documentId) {
        setSelectedDocument(null);
      }

      setDeleteConfirmation({ isOpen: false, documentId: null, documentName: '' });
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to delete document. Please try again.'
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteConfirmation.documentId, deleteDocument, selectedDocument]);

  /**
   * Cancel delete confirmation
   */
  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmation({ isOpen: false, documentId: null, documentName: '' });
  }, []);

  /**
   * Open upload version dialog
   */
  const handleUploadVersionClick = useCallback((documentId: string) => {
    const doc = getDocumentById(documentId);
    setUploadVersionState({
      isOpen: true,
      documentId,
      documentName: doc?.processedFileName || 'document',
    });
    // Trigger file input click
    setTimeout(() => {
      versionUploadRef.current?.click();
    }, 100);
  }, [getDocumentById]);

  /**
   * Handle version file selection
   * Requirement 12.3: Document upload operations are simulated in demo mode
   */
  const handleVersionFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !uploadVersionState.documentId) {
      setUploadVersionState({ isOpen: false, documentId: null, documentName: '' });
      return;
    }

    const doc = getDocumentById(uploadVersionState.documentId);
    if (!doc) {
      setActionError('Document not found. Please refresh and try again.');
      setUploadVersionState({ isOpen: false, documentId: null, documentName: '' });
      return;
    }

    try {
      // Upload as new version of the existing document
      await uploadNewVersion(uploadVersionState.documentId, file);

      // Update selected document if it was the one being versioned
      if (selectedDocument?.processId === uploadVersionState.documentId) {
        const updatedDoc = getDocumentById(uploadVersionState.documentId);
        if (updatedDoc) {
          setSelectedDocument(updatedDoc);
        }
      }
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to upload new version. Please try again.'
      );
    } finally {
      setUploadVersionState({ isOpen: false, documentId: null, documentName: '' });
      // Reset file input
      if (event.target) {
        event.target.value = '';
      }
    }
  }, [uploadVersionState.documentId, getDocumentById, uploadNewVersion, selectedDocument]);

  /**
   * Custom upload handler that wraps the context's uploadDocument
   * to return the expected UploadedDocument type
   */
  const handleUpload = useCallback(async (
    file: File,
    metadata: DocumentMetadata
  ): Promise<UploadedDocument> => {
    // Use schema ID from environment config
    const schemaId = config.defaultSchemaId;
    if (!schemaId) {
      throw new Error('Schema ID not configured. Please set VITE_DEFAULT_SCHEMA_ID environment variable.');
    }

    // Use metadata.name as the metadataId for the API
    await uploadDocument(file, schemaId, metadata.name);

    // Return a mock UploadedDocument since the context handles the actual storage
    // The DocumentUploader will call onUploadComplete which triggers a refresh
    const uploadedDoc: UploadedDocument = {
      id: `doc-${Date.now()}`,
      name: metadata.name,
      classification: metadata.classification,
      size: file.size,
      mimeType: file.type,
      uploadedAt: new Date().toISOString(),
      blobUrl: URL.createObjectURL(file),
    };

    return uploadedDoc;
  }, [uploadDocument]);

  /**
   * Handle successful document upload
   */
  const handleUploadComplete = useCallback((_uploadedDoc: UploadedDocument) => {
    setActionError(null);
    // Refresh documents to get the full document data
    refreshDocuments();
  }, [refreshDocuments]);

  /**
   * Handle upload error
   */
  const handleUploadError = useCallback((err: Error) => {
    setActionError(err.message);
  }, []);

  /**
   * Clear action error
   */
  const handleClearActionError = useCallback(() => {
    setActionError(null);
  }, []);

  /**
   * Toggle document selection for comparison
   * Limits selection to 2 completed documents
   */
  const handleToggleCompareSelect = useCallback((processId: string) => {
    setSelectedForCompare((prev) => {
      const next = new Set(prev);
      if (next.has(processId)) {
        next.delete(processId);
      } else if (next.size < 2) {
        next.add(processId);
      }
      return next;
    });
  }, []);

  /**
   * Navigate to comparison page with the two selected documents
   */
  const handleCompare = useCallback(() => {
    const ids = Array.from(selectedForCompare);
    if (ids.length === 2) {
      navigate(`/evidence-extractor/compare/${ids[0]}/${ids[1]}`);
    }
  }, [selectedForCompare, navigate]);

  // ============================================================================
  // Processing Workflow Handlers (Req 3.2, 3.5, 3.6)
  // ============================================================================

  /**
   * Start processing documents for SORA evidence extraction
   * Calls Content Processor API or mock service based on demo mode
   */
  const handleStartProcessing = useCallback(async () => {
    if (documents.length === 0) {
      setActionError('No documents available for processing.');
      return;
    }

    // Reset processing state and show progress modal
    setProcessingState({
      isProcessing: true,
      showProgress: true,
      status: 'processing',
      progress: 0,
      currentStep: 'Preparing documents for processing...',
      error: null,
      extractionId: null,
    });
    setActionError(null);

    try {
      const documentIds = documents.map((doc) => doc.processId);
      let extractionResults: ExtractionResults;

      if (isDemoMode() && mockService) {
        // Demo mode: simulate processing with mock data
        // Update progress through stages
        setProcessingState((prev) => ({
          ...prev,
          progress: 25,
          currentStep: 'Uploading documents...',
        }));

        await new Promise((resolve) => setTimeout(resolve, 800));

        setProcessingState((prev) => ({
          ...prev,
          progress: 50,
          currentStep: 'Analyzing document content...',
        }));

        await new Promise((resolve) => setTimeout(resolve, 1000));

        setProcessingState((prev) => ({
          ...prev,
          progress: 75,
          currentStep: 'Extracting SORA evidence...',
        }));

        // Simulate processing
        await mockService.simulateProcessing();

        setProcessingState((prev) => ({
          ...prev,
          progress: 90,
          currentStep: 'Validating extraction results...',
        }));

        await new Promise((resolve) => setTimeout(resolve, 500));

        // Get mock extraction results
        extractionResults = mockService.getExtractionResults();

        // Update to completed state
        setProcessingState({
          isProcessing: false,
          showProgress: true,
          status: 'completed',
          progress: 100,
          currentStep: 'Processing complete!',
          error: null,
          extractionId: extractionResults.processId,
        });
      } else if (apiClient) {
        // Production mode: call Content Processor API
        setProcessingState((prev) => ({
          ...prev,
          progress: 10,
          currentStep: 'Initiating document processing...',
        }));

        // Start processing job
        const processResponse = await apiClient.processDocuments(documentIds);

        if (!isApiSuccess(processResponse)) {
          throw new Error(getErrorMessage(processResponse.error));
        }

        const job = processResponse.data;

        // Poll for processing status
        let attempts = 0;
        const maxAttempts = 60; // 5 minutes with 5-second intervals
        const pollInterval = 5000;

        while (attempts < maxAttempts) {
          const statusResponse = await apiClient.getProcessingStatus(job.jobId);

          if (!isApiSuccess(statusResponse)) {
            throw new Error(getErrorMessage(statusResponse.error));
          }

          const status = statusResponse.data;

          // Update progress based on status
          setProcessingState((prev) => ({
            ...prev,
            progress: Math.min(90, 10 + (attempts / maxAttempts) * 80),
            currentStep: getProcessingStepMessage(status),
          }));

          if (status === 'completed') {
            break;
          }

          if (status === 'failed') {
            throw new Error('Document processing failed. Please try again.');
          }

          await new Promise((resolve) => setTimeout(resolve, pollInterval));
          attempts++;
        }

        if (attempts >= maxAttempts) {
          throw new Error('Processing timeout. Please try again later.');
        }

        // Get extraction results
        setProcessingState((prev) => ({
          ...prev,
          progress: 95,
          currentStep: 'Retrieving extraction results...',
        }));

        const resultsResponse = await apiClient.getExtractionResults(job.jobId);

        if (!isApiSuccess(resultsResponse)) {
          throw new Error(getErrorMessage(resultsResponse.error));
        }

        extractionResults = resultsResponse.data;

        // Update to completed state
        setProcessingState({
          isProcessing: false,
          showProgress: true,
          status: 'completed',
          progress: 100,
          currentStep: 'Processing complete!',
          error: null,
          extractionId: extractionResults.processId,
        });
      } else {
        throw new Error('No processing service available. Please check your configuration.');
      }

      // Store extraction results in context (Req 3.6)
      setCurrentExtraction(extractionResults);
    } catch (err) {
      // Handle processing errors (Req 3.5)
      const errorMessage = err instanceof Error
        ? err.message
        : 'An unexpected error occurred during processing.';

      setProcessingState({
        isProcessing: false,
        showProgress: true,
        status: 'failed',
        progress: 0,
        currentStep: '',
        error: errorMessage,
        extractionId: null,
      });

      console.error('Processing error:', err);
    }
  }, [documents, apiClient, mockService, setCurrentExtraction]);

  /**
   * Get a user-friendly message for the current processing step
   */
  const getProcessingStepMessage = (status: ProcessingStatus): string => {
    switch (status) {
      case 'pending':
        return 'Waiting to start processing...';
      case 'processing':
        return 'Extracting SORA evidence from documents...';
      case 'completed':
        return 'Processing complete!';
      case 'failed':
        return 'Processing failed.';
      default:
        return 'Processing...';
    }
  };

  /**
   * Navigate to review page after successful processing
   */
  const handleNavigateToReview = useCallback(() => {
    if (processingState.extractionId) {
      // Close the progress modal
      setProcessingState(INITIAL_PROCESSING_STATE);
      // Navigate to review page with extraction ID
      navigate(`/evidence-extractor/review/${processingState.extractionId}`);
    }
  }, [processingState.extractionId, navigate]);

  /**
   * Retry processing after failure
   */
  const handleRetryProcessing = useCallback(() => {
    setProcessingState(INITIAL_PROCESSING_STATE);
    handleStartProcessing();
  }, [handleStartProcessing]);

  /**
   * Cancel/close the processing progress modal
   */
  const handleCancelProcessing = useCallback(() => {
    // Only allow canceling if not actively processing
    if (!processingState.isProcessing) {
      setProcessingState(INITIAL_PROCESSING_STATE);
    }
  }, [processingState.isProcessing]);

  // ============================================================================
  // Document Details View (when :id param is present)
  // ============================================================================

  if (id) {
    const document = getDocumentById(id);

    if (!document && !isLoading) {
      return (
        <div className={styles.container}>
          <div className={styles.header}>
            <Link to="/evidence-extractor" className={styles.backLink}>
              <ArrowLeftRegular className={styles.backIcon} />
              <span>Back to Documents</span>
            </Link>
            <h1 className={styles.title}>Document Not Found</h1>
          </div>
          <div className={styles.errorState}>
            <Warning24Regular className={styles.errorIcon} />
            <p className={styles.errorText}>
              The document you're looking for could not be found.
            </p>
            <Link to="/evidence-extractor" className={styles.errorLink}>
              Return to Documents
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <Link to="/evidence-extractor" className={styles.backLink}>
            <ArrowLeftRegular className={styles.backIcon} />
            <span>Back to Documents</span>
          </Link>
          <h1 className={styles.title}>{document?.processedFileName || 'Loading...'}</h1>
          {document && (
            <p className={styles.subtitle}>
              Uploaded {new Date(document.importedTime).toLocaleDateString('en-GB')}
            </p>
          )}
        </div>

        {document && (
          <>
            {/* Document Actions */}
            <div className={styles.documentActions}>
              <button
                className={styles.actionButton}
                onClick={() => handleDownload(document.processId)}
              >
                <ArrowDownload20Regular className={styles.actionButtonIcon} />
                Download
              </button>
              <button
                className={styles.actionButton}
                onClick={() => handleUploadVersionClick(document.processId)}
              >
                <ArrowUpload20Regular className={styles.actionButtonIcon} />
                Upload New Version
              </button>
              <button
                className={`${styles.actionButton} ${styles.deleteButton}`}
                onClick={() => handleDeleteClick(document.processId)}
              >
                <Delete20Regular className={styles.actionButtonIcon} />
                Delete
              </button>
            </div>

            {/* Version History */}
            <div className={styles.versionHistorySection}>
              <VersionHistory
                versions={[]}
                currentVersion={1}
                documentName={document.processedFileName || 'document'}
                onDownloadVersion={handleVersionDownload}
              />
            </div>
          </>
        )}
      </div>
    );
  }

  // ============================================================================
  // Main Document List View
  // ============================================================================

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h1 className={styles.title}>SORA Evidence Extractor</h1>
        <p className={styles.subtitle}>
          Upload and manage your operational documentation.
        </p>
      </div>

      {/* Error Display */}
      {(error || actionError) && (
        <div className={styles.errorBanner}>
          <Warning24Regular className={styles.errorBannerIcon} />
          <span className={styles.errorBannerText}>
            {actionError || error?.message}
          </span>
          <button
            className={styles.errorBannerClose}
            onClick={actionError ? handleClearActionError : clearError}
            aria-label="Dismiss error"
          >
            <Dismiss24Regular />
          </button>
        </div>
      )}

      {/* Main Content Layout */}
      <div className={styles.mainContent}>
        {/* Left Column: Upload and Document List */}
        <div className={styles.leftColumn}>
          {/* Document Uploader */}
          <section className={styles.uploadSection}>
            <h2 className={styles.sectionTitle}>Upload Document</h2>
            <DocumentUploader
              onUploadComplete={handleUploadComplete}
              onUploadError={handleUploadError}
              uploadHandler={handleUpload}
            />
          </section>

          {/* Document List */}
          <section className={styles.listSection}>
            <div className={styles.listHeader}>
              <h2 className={styles.sectionTitle}>Uploaded Documents</h2>
              <div className={styles.listHeaderActions}>
                {selectedForCompare.size === 2 && (
                  <button
                    className={styles.compareButton}
                    onClick={handleCompare}
                  >
                    <DocumentMultiple20Regular />
                    Compare Selected
                  </button>
                )}
                {selectedForCompare.size > 0 && selectedForCompare.size < 2 && (
                  <span className={styles.compareHint}>
                    Select 1 more document to compare
                  </span>
                )}
                <span className={styles.documentCount}>
                  {documents.length} document{documents.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <DocumentList
              documents={documents}
              onSelect={handleDocumentSelect}
              onDelete={handleDeleteClick}
              onDownload={handleDownload}
              isLoading={isLoading}
              selectedIds={selectedForCompare}
              onToggleSelect={handleToggleCompareSelect}
            />
          </section>

          {/* Processing Button (Req 3.1)
          <section className={styles.processingSection}>
            <ProcessingButton
              onClick={handleStartProcessing}
              hasDocuments={documents.length > 0}
              isProcessing={processingState.isProcessing}
            />
          </section> */}
        </div>

        {/* Right Column: Document Details Panel */}
        {selectedDocument && (
          <aside className={styles.rightColumn}>
            <div className={styles.documentDetailsPanel}>
              <div className={styles.panelHeader}>
                <h3 className={styles.panelTitle}>Document Details</h3>
                <button
                  className={styles.panelCloseButton}
                  onClick={handleCloseVersionHistory}
                  aria-label="Close document details"
                >
                  <Dismiss24Regular />
                </button>
              </div>
              <div className={styles.detailsContent}>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>File Name</span>
                  <span className={styles.detailValue}>
                    {selectedDocument.processedFileName || 'Unnamed'}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Status</span>
                  <span className={`${styles.detailValue} ${styles[`status${selectedDocument.status.charAt(0).toUpperCase() + selectedDocument.status.slice(1)}`]}`}>
                    {selectedDocument.status.charAt(0).toUpperCase() + selectedDocument.status.slice(1)}
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Coverage</span>
                  <span className={styles.detailValue}>
                    {Math.round(selectedDocument.schemaScore * 100)}%
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Confidence</span>
                  <span className={styles.detailValue}>
                    {Math.round(selectedDocument.entityScore * 100)}%
                  </span>
                </div>
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Imported</span>
                  <span className={styles.detailValue}>
                    {new Date(selectedDocument.importedTime).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                {selectedDocument.processedTime && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Processing Time</span>
                    <span className={styles.detailValue}>
                      {selectedDocument.processedTime}
                    </span>
                  </div>
                )}
                {selectedDocument.lastModifiedBy && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Last Modified By</span>
                    <span className={styles.detailValue}>
                      {selectedDocument.lastModifiedBy}
                    </span>
                  </div>
                )}
                {selectedDocument.comment && (
                  <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Comment</span>
                    <span className={styles.detailValue}>
                      {selectedDocument.comment}
                    </span>
                  </div>
                )}
                <div className={styles.detailItem}>
                  <span className={styles.detailLabel}>Process ID</span>
                  <span className={`${styles.detailValue} ${styles.processId}`}>
                    {selectedDocument.processId}
                  </span>
                </div>
              </div>
              <div className={styles.panelActions}>
                <button
                  className={`${styles.panelActionButton} ${styles.primaryButton}`}
                  onClick={() => navigate(`/evidence-extractor/review/${selectedDocument.processId}`)}
                  disabled={selectedDocument.status !== 'completed'}
                  title={selectedDocument.status !== 'completed' ? 'Document must be processed before viewing extraction' : 'View extracted evidence'}
                >
                  <Eye20Regular />
                  View Extraction
                </button>
                <button
                  className={styles.panelActionButton}
                  onClick={() => handleDownload(selectedDocument.processId)}
                  disabled={selectedDocument.status !== 'completed'}
                >
                  <ArrowDownload20Regular />
                  Download
                </button>
                <button
                  className={`${styles.panelActionButton} ${styles.deleteButton}`}
                  onClick={() => handleDeleteClick(selectedDocument.processId)}
                >
                  <Delete20Regular />
                  Delete
                </button>
              </div>
            </div>
          </aside>
        )}
      </div>

      {/* Hidden file input for version uploads */}
      <input
        ref={versionUploadRef}
        type="file"
        accept=".pdf,.docx,.doc"
        onChange={handleVersionFileSelect}
        className={styles.hiddenInput}
        aria-hidden="true"
      />

      {/* Delete Confirmation Dialog */}
      {deleteConfirmation.isOpen && (
        <div className={styles.dialogOverlay} role="dialog" aria-modal="true">
          <div className={styles.dialog}>
            <h3 className={styles.dialogTitle}>Delete Document</h3>
            <p className={styles.dialogMessage}>
              Are you sure you want to delete "{deleteConfirmation.documentName}"?
              This action cannot be undone.
            </p>
            <div className={styles.dialogActions}>
              <button
                className={styles.dialogCancelButton}
                onClick={handleDeleteCancel}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                className={styles.dialogDeleteButton}
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Processing Progress Modal (Req 3.2, 3.4, 3.5) */}
      {processingState.showProgress && (
        <div className={styles.dialogOverlay} role="dialog" aria-modal="true">
          <div className={styles.processingDialog}>
            <ProcessingProgress
              status={processingState.status}
              progress={processingState.progress}
              currentStep={processingState.currentStep}
              error={processingState.error || undefined}
              onNavigateToReview={handleNavigateToReview}
              onRetry={handleRetryProcessing}
              onCancel={handleCancelProcessing}
              extractionId={processingState.extractionId || undefined}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsPage;
